import { apiRequests } from '../utilities/axios-factory'
import store from '../store'
import { generateKeywordsQueryForOr } from '@smarttransit/common'

function buildEventsWhere ({ keywords, dateFrom, dateTo }) {
  let where = {}
  const andConditions = []

  if (keywords) {
    andConditions.push({ or: generateKeywordsQueryForOr(keywords, ['name']) })
  }

  if (dateFrom || dateTo) {
    const range = {}

    if (dateFrom) {
      range.gte = dateFrom
    }

    if (dateTo) {
      range.lte = dateTo
    }

    andConditions.push({ eventDate: range })
  }

  if (andConditions.length) {
    where.and = andConditions
  }

  if (!Object.keys(where).length) {
    where = undefined
  }

  return where
}

export function findEvents ({ keywords, dateFrom, dateTo, limit, offset, order }) {
  const filter = {
    limit,
    offset,
    order: order || 'eventDate DESC'
  }

  const where = buildEventsWhere({ keywords, dateFrom, dateTo })

  if (where) {
    filter.where = where
  }

  return apiRequests.get('/v1/transportation-profile-event/', {
    params: { filter },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function findTotalEvents ({ keywords, dateFrom, dateTo }) {
  const where = buildEventsWhere({ keywords, dateFrom, dateTo })

  return apiRequests.get('/v1/transportation-profile-event/count', {
    params: { where },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return (result.data && result.data.count) || 0
  })
}

export function getArchivedEvents ({ keywords, dateFrom, dateTo, limit, offset, order }) {
  const filter = {
    limit,
    offset,
    order: order || 'eventDate DESC',
    where: {
      and: []
    }
  }

  const baseWhere = buildEventsWhere({ keywords, dateFrom, dateTo })
  if (baseWhere && baseWhere.and && baseWhere.and.length) {
    filter.where.and = filter.where.and.concat(baseWhere.and)
  }

  filter.where.and.push({ dateArchived: { neq: null } })

  return apiRequests.get('/v1/raw-transportation-profile-event/', {
    params: { filter },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function findTotalArchivedEvents ({ keywords, dateFrom, dateTo }) {
  const baseWhere = buildEventsWhere({ keywords, dateFrom, dateTo }) || {}
  const andConditions = baseWhere.and ? baseWhere.and.slice() : []

  andConditions.push({ dateArchived: { neq: null } })

  const where = andConditions.length ? { and: andConditions } : { dateArchived: { neq: null } }

  return apiRequests.get('/v1/raw-transportation-profile-event/count', {
    params: { where },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return (result.data && result.data.count) || 0
  })
}

export function getEvent ({ id }) {
  return apiRequests.get('/v1/raw-transportation-profile-event/' + id + '/', {
    params: { filter: { include: { transportationProfileEventProfiles: 'transportationProfile' } } },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function createEvent (payload) {
  return apiRequests.post('/v1/transportation-profile-event/', payload, {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function updateEvent ({ id, ...payload }) {
  return apiRequests.patch('/v1/transportation-profile-event/' + id + '/', payload, {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function deleteEvent ({ id }) {
  return apiRequests.delete('/v1/transportation-profile-event/' + id + '/', {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function archiveEvent ({ id }) {
  return apiRequests.post('/v1/transportation-profile-event/' + id + '/archived/', {}, {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function unarchiveEvent ({ id }) {
  return apiRequests.put('/v1/transportation-profile-event/' + id + '/unarchived/', {}, {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function attachProfilesToEvent ({ id, transportationProfileIds }) {
  return apiRequests.post('/v1/transportation-profile-event/' + id + '/transportation-profiles/', {
    transportationProfileIds
  }, {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function detachProfileFromEvent ({ id, transportationProfileId }) {
  return apiRequests.delete('/v1/transportation-profile-event/' + id + '/transportation-profiles/' + transportationProfileId + '/', {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export function getEventCapacityStatus ({ id }) {
  return apiRequests.get('/v1/transportation-profile-event/' + id + '/capacity/', {
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 * @params {object} options
 * @param {string} options.id - Event ID
 * @returns {Promise<{profiles: array, totalCapacityFromProfiles: number}[]>}
 */
export function getEventTransportationProfiles ({ id }) {
  const filter = {
    include: 'transportationProfile'
  }

  return apiRequests.get('/v1/transportation-profile-event/' + id + '/transportation-profile-event-profiles', {
    params: { filter },
    headers: { Authorization: store.getters.getTokenId }
  }).then((result) => {
    const records = result.data || []
    const profiles = []
    let totalCapacityFromProfiles = 0

    records.forEach((record) => {
      const tp = record.transportationProfile

      if (tp && tp.id) {
        const profile = {
          id: tp.id,
          licensePlateNumber: tp.licensePlateNumber,
          shortcodeId: tp.shortcodeId
        }

        profiles.push(profile)

        const vehicleMeta = tp.metadata && tp.metadata.vehicle

        if (vehicleMeta && vehicleMeta.passengerLoad) {
          totalCapacityFromProfiles += Number(vehicleMeta.passengerLoad || 0)
        }
      }
    })

    return {
      profiles,
      totalCapacityFromProfiles
    }
  })
}

export function getEventMetrics ({ id }) {
  return getEventCapacityStatus({ id }).then((capacityStatus) => {
    return {
      capacityStatus
    }
  })
}
